/**
 * Marketing Report Builder.
 *
 * Similar structure to PlaygroundBuilder but for marketing reports.
 * Uses useMarketingState for URL-driven config.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────┐
 *   │  TopBar: title + actions (Save, Share, Run)     │
 *   ├─────────────────────────────────────────────────┤
 *   │  MarketingConfigPanel (collapsible)             │
 *   ├─────────────────────────────────────────────────┤
 *   │  MarketingResultsPanel                          │
 *   └─────────────────────────────────────────────────┘
 */
import { useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useMarketingState } from './useMarketingState';
import type { MarketingState } from './useMarketingState';
import { MarketingConfigPanel } from './MarketingConfigPanel';
import { MarketingResultsPanel } from './MarketingResultsPanel';
import { BuilderShell } from '@/components/builder/BuilderShell';
import { toast } from '@/components/ui/Toast';
import { api } from '@/services/api';
import { endpoints } from '@/services/endpoints';
import { parseISO, differenceInDays, addDays, format as fmtDate } from 'date-fns';
import { useSavedReport } from '@/hooks/useSavedReport';
import { useSectionCache } from '@/hooks/useSectionCache';

// Marketing runs fan out to BigQuery + the ad-platform APIs (Meta/Google/Apple/
// TikTok). On a cold cache the first run can legitimately take longer than the
// default 30s client timeout, which would abort the request even though the
// backend keeps computing and fills its caches. Give the two heavy endpoints a
// larger budget so the first run actually returns instead of timing out.
//
// These POSTs also pass `direct: true` so they hit Cloud Run directly instead of
// the `/api` Firebase Hosting rewrite, whose hard 60s cap would 504 a cold run
// before this budget elapses. (In dev the direct origin is empty, so it stays on
// the Vite proxy, which has no timeout.)
const MARKETING_REQUEST_TIMEOUT_MS = 120_000;

export function MarketingBuilder() {
  const { reportId } = useParams();
  const { state, update, reset, canRun, toApiPayload, dates, comparisonDates, searchParams } = useMarketingState();
  const resultsRef = useRef<HTMLDivElement>(null);

  // ─── Load saved report ────────────────────────────────────────────────
  const { savedReport } = useSavedReport<MarketingState>({
    reportId,
    sectionId: 'marketing',
    searchParams,
    update,
    skipKeys: ['type'],
    mapConfig: useCallback((config: Record<string, unknown>) => {
      const INTERVAL_LONG_TO_SHORT: Record<string, string> = {
        hours: 'h', days: 'd', weeks: 'w', months: 'm',
      };
      const rawMetrics = config.selectedMetrics ?? config.metrics ?? '';
      const metrics: string[] = Array.isArray(rawMetrics)
        ? rawMetrics
        : typeof rawMetrics === 'string' && rawMetrics
          ? rawMetrics.split(',').filter(Boolean)
          : [];
      return {
        breakdown: ((config.rowBreakdown as string) || (config.breakdown as string) || 'campaign_name'),
        metrics: metrics.length > 0 ? metrics : undefined!,
        preset: ((config.datePreset as string) || (config.preset as string) || ''),
        start: ((config.startDate as string) || (config.start as string) || ''),
        end: ((config.endDate as string) || (config.end as string) || ''),
        chart: (((config.chartType as string) === 'time_series' ? 'ts' : (config.chartType as string)) || (config.chart as string) || 'pivot') as MarketingState['chart'],
        interval: (INTERVAL_LONG_TO_SHORT[(config.timeInterval as string)] || (config.interval as string) || 'd'),
        cumulative: Boolean(config.cumulative),
        filters: typeof config.filters === 'string'
          ? config.filters
          : Array.isArray(config.filters) && config.filters.length > 0
            ? btoa(JSON.stringify(config.filters))
            : '',
      };
    }, []),
  });

  // ─── Section cache (preserve results across navigation) ─────────────
  const [cachedData, setCachedData] = useSectionCache<unknown>('marketing-builder');

  // ─── Run query ────────────────────────────────────────────────────────
  const queryMutation = useMutation({
    mutationFn: async () => {
      const payload = toApiPayload();
      if (state.chart === 'ts') {
        // Fire comparison query in parallel when comparison dates are set
        let comparisonPromise: ReturnType<typeof api.post> | null = null;
        if (comparisonDates) {
          const pStart = parseISO(dates.startDate);
          const pEnd = parseISO(dates.endDate);
          const rangeDays = differenceInDays(pEnd, pStart);
          const cmpStartDate = comparisonDates.startDate;
          const cmpEndDate = fmtDate(addDays(parseISO(cmpStartDate), rangeDays), 'yyyy-MM-dd');
          comparisonPromise = api.post(endpoints.marketing.timeSeries(), {
            ...payload,
            startDate: cmpStartDate,
            endDate: cmpEndDate,
          }, MARKETING_REQUEST_TIMEOUT_MS, true);
        }

        const mainResult = await api.post(endpoints.marketing.timeSeries(), payload, MARKETING_REQUEST_TIMEOUT_MS, true);
        const comparisonResult = comparisonPromise ? await comparisonPromise : undefined;

        return {
          ...(mainResult as Record<string, unknown>),
          _comparisonData: comparisonResult ?? undefined,
        };
      } else {
        // Fire main + comparison queries in parallel when comparison dates are set
        const comparisonPromise = comparisonDates
          ? api.post(endpoints.marketing.pivot(), {
              ...payload,
              startDate: comparisonDates.startDate,
              endDate: comparisonDates.endDate,
            }, MARKETING_REQUEST_TIMEOUT_MS, true)
          : null;

        const mainResult = await api.post(endpoints.marketing.pivot(), payload, MARKETING_REQUEST_TIMEOUT_MS, true);
        const comparisonResult = comparisonPromise ? await comparisonPromise : undefined;

        return {
          ...(mainResult as Record<string, unknown>),
          _comparisonData: comparisonResult ?? undefined,
        };
      }
    },
    onSuccess: (data) => {
      setCachedData(data);
    },
  });

  // Effective data: mutation data takes priority, then cached
  const effectiveData = queryMutation.data ?? cachedData;
  const effectiveHasRun = queryMutation.isSuccess || queryMutation.isError || !!cachedData;

  const handleRun = useCallback(() => {
    if (canRun) {
      queryMutation.mutate();
    }
  }, [canRun, queryMutation]);

  // ─── Export as Image ─────────────────────────────────────────────────
  const [isExporting, setIsExporting] = useState(false);
  const handleExport = useCallback(async () => {
    if (!resultsRef.current) return;
    setIsExporting(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(resultsRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
      });
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `marketing-report-${new Date().toISOString().split('T')[0]}.png`;
      a.click();
      toast.success('Image exported');
    } catch (err) {
      console.error('Export failed:', err);
      toast.error('Export failed');
    } finally {
      setIsExporting(false);
    }
  }, []);

  // ─── Config for saving ────────────────────────────────────────────────
  const saveConfig = {
    ...state,
    reportType: 'marketing',
  } as unknown as Record<string, unknown>;

  return (
    <BuilderShell
      title="New Marketing Report"
      subtitle="Marketing"
      sectionId="marketing"
      sectionRoute="/marketing"
      reportId={reportId}
      savedReport={savedReport as { report?: { name?: string; description?: string } } | undefined}
      onReset={reset}
      onRun={handleRun}
      isRunning={queryMutation.isPending}
      canRun={canRun}
      configForSave={saveConfig}
      configPanel={<MarketingConfigPanel state={state} onChange={update} dates={dates} />}
      extraActions={
        <Button
          variant="ghost"
          size="sm"
          icon={<Download />}
          onClick={handleExport}
          loading={isExporting}
          disabled={!queryMutation.isSuccess}
        >
          Export
        </Button>
      }
    >
      <div ref={resultsRef}>
        <MarketingResultsPanel
          chartType={state.chart}
          isLoading={queryMutation.isPending}
          isError={queryMutation.isError}
          error={queryMutation.error}
          data={effectiveData}
          hasRun={effectiveHasRun}
          canRun={canRun}
          onRun={handleRun}
          selectedMetrics={state.metrics}
          comparisonData={(effectiveData as Record<string, unknown> | undefined)?._comparisonData as Record<string, unknown> | undefined}
        />
      </div>
    </BuilderShell>
  );
}
